import config from 'mewe/config';
import { GoogleAuthClientId } from 'mewe/constants';
import Verbose from 'mewe/utils/verbose';
const verbose = Verbose({ prefix: '[GoogleAuth]' }).log;

export class GoogleAuth {
  static isInitialized = false;
  static initializationPromise = null;
  static isSDKLoaded = false;
  static sdkLoadPromise = null;

  static clientId = (config.environment === 'prod' && !config.isQA) ? GoogleAuthClientId.PROD : GoogleAuthClientId.DEV;

  // Shared popup settings
  static popupName = 'google-oauth';
  static getPopupFeatures(width = 500, height = 600) {
    const left = Math.max(0, Math.floor((window.screen.width - width) / 2));
    const top = Math.max(0, Math.floor((window.screen.height - height) / 2));
    return [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'menubar=no',
      'toolbar=no',
      'location=no',
      'status=no',
      'resizable=yes',
      'scrollbars=yes'
    ].join(',');
  }

  static loadGoogleSDK() {
    if (this.isSDKLoaded) {
      return Promise.resolve();
    }

    if (this.sdkLoadPromise) {
      return this.sdkLoadPromise;
    }

    this.sdkLoadPromise = new Promise((resolve, reject) => {
      // Check if SDK is already loaded
      if (window.google && window.google.accounts) {
        this.isSDKLoaded = true;
        resolve();
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.isSDKLoaded = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services SDK'));
      };

      // Add script to document head
      document.head.appendChild(script);
    });

    return this.sdkLoadPromise;
  }

  static async initialize() {
    if (this.isInitialized) {
      return Promise.resolve();
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = new Promise(async (resolve, reject) => {
      try {
        // First, ensure the SDK is loaded
        await this.loadGoogleSDK();

        // Wait for Google SDK to be fully available
        const checkGoogleSDK = () => {
          if (window.google && window.google.accounts) {
            this.isInitialized = true;
            resolve();
          } else {
            setTimeout(checkGoogleSDK, 100);
          }
        };
        checkGoogleSDK();
      } catch (error) {
        reject(error);
      }
    });

    return this.initializationPromise;
  }

  static generateNonce(length = 32) {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const values = window.crypto.getRandomValues(new Uint32Array(length));
    for (let i = 0; i < length; i++) {
      result += charset[values[i] % charset.length];
    }
    return result;
  }

  static openGoogleOAuthPopup(customParams = {}) {
    verbose('🔄 Opening OAuth popup...');

    const clientId = this.clientId;
    const redirectUri = window.location.origin + '/auth/google/callback';
    const scope = 'openid email profile';
    const responseType = 'code id_token';
    const nonce = GoogleAuth.generateNonce();
    const popupName = GoogleAuth.popupName;
    const popupFeatures = GoogleAuth.getPopupFeatures(500, 600);

    // Collect all parameters to pass through OAuth flow
    const currentParams = new URLSearchParams(window.location.search);
    // Exclude non-serializable values from state, like Window references
    const { preOpenedWindow, ...serializableParams } = customParams || {};
    const stateData = {
      nonce: nonce,
      returnUrl: '/myworld',
      // Add any custom serializable parameters passed to this function
      ...serializableParams
    };

    // Encode the state data
    const state = btoa(JSON.stringify(stateData));

    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=${responseType}&scope=${encodeURIComponent(scope)}&prompt=select_account&nonce=${nonce}&state=${encodeURIComponent(state)}`;

    verbose('🔗 OAuth URL:', url);
    const popupCandidate = customParams.preOpenedWindow;
    const popup = popupCandidate && !popupCandidate.closed
      ? popupCandidate
      : window.open('about:blank', popupName, popupFeatures);

    // Check if popup was blocked
    if (!popup) {
      throw new Error('Popup was blocked by browser. Please allow popups for this site.');
    }

    // Navigate the (possibly pre-opened) popup to the OAuth URL
    try {
      // Best-effort sizing/moving before navigation (Safari may ignore)
      try {
        popup.resizeTo(500, 600);
        const left = Math.max(0, Math.floor((window.screen.width - 500) / 2));
        const top = Math.max(0, Math.floor((window.screen.height - 600) / 2));
        popup.moveTo(left, top);
      } catch (_) {}

      popup.location.href = url;
      popup.focus?.();
    } catch (_) {
      // ignore navigation focus errors
    }

    // Set up a listener to detect when the popup closes
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        // Popup was closed, check if user is now authenticated
        // This is a fallback in case the redirect doesn't work
        setTimeout(() => {
          // You could add logic here to check if authentication succeeded
          // For now, we'll rely on the callback route to handle everything
        }, 1000);
      }
    }, 1000);

    return popup;
  }

  static async hybridSignIn(onSuccess, onError, customParams = {}) {
    await this.initialize();

    let popupOpened = false;
    let fedcmSupported = 'FedCM' in window;

    // Run comprehensive debug
    await this.debugGoogleAuthSetup();

    window.google.accounts.id.initialize({
      client_id: this.clientId,
      callback: (response) => {
        verbose('✅ Google Auth Success:', response);
        if (response.credential) {
          onSuccess(response);
        } else {
          onError(new Error('No credential returned from Google'));
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    // Try to detect if One Tap can be shown
    // If FedCM is not supported, open popup immediately
    if (!fedcmSupported) {
      verbose('⚠️ FedCM not supported, opening OAuth popup immediately');
      GoogleAuth.openGoogleOAuthPopup(customParams);
      popupOpened = true;
      return;
    }

    verbose('🎯 Attempting to show One Tap...');

    // Otherwise, try One Tap and fallback if needed
    window.google.accounts.id.prompt((notification) => {
      verbose('📋 One Tap notification:', notification);

      if (notification.isNotDisplayed && notification.isNotDisplayed()) {
        const reason = notification.getNotDisplayedReason();
        verbose('❌ One Tap not displayed, reason:', reason);

        if (reason === 'no_credentials_available') {
          verbose('👤 No Google credentials available, opening OAuth popup');
          // Open OAuth popup as fallback (may be blocked if not in click handler)
          if (!popupOpened) {
            GoogleAuth.openGoogleOAuthPopup(customParams);
          }
        } else {
          verbose('🚫 One Tap blocked for reason:', reason);
          onError(new Error(`One Tap not available: ${reason}`));
        }
      } else if (notification.isSkippedMoment && notification.isSkippedMoment()) {
        verbose('⏭️ One Tap skipped');
        onError(new Error('One Tap was skipped'));
      } else if (notification.isDismissedMoment && notification.isDismissedMoment()) {
        verbose('❌ One Tap dismissed by user');
        onError(new Error('One Tap was dismissed'));
      } else {
        verbose('✅ One Tap should be displayed');
      }
    });
  }

  static getChromeVersion() {
    const userAgent = navigator.userAgent;
    const chromeMatch = userAgent.match(/Chrome\/(\d+)/);
    return chromeMatch ? chromeMatch[1] : 'Unknown';
  }

  static async debugGoogleAuthSetup() {
    verbose('🔍 Comprehensive Google Auth Debug:');
    verbose('=====================================');

    // Basic environment checks
    verbose('1. Environment:');
    verbose('   - Protocol:', window.location.protocol);
    verbose('   - Hostname:', window.location.hostname);

    // Browser checks
    verbose('2. Browser:');
    verbose('   - User Agent:', navigator.userAgent);
    verbose('   - Chrome Version:', this.getChromeVersion());
    verbose('   - FedCM Support:', 'FedCM' in window);

    // Google SDK checks
    verbose('3. Google SDK:');
    verbose('   - Google object exists:', !!window.google);
    verbose('   - Google.accounts exists:', !!(window.google && window.google.accounts));
    verbose('   - Google.accounts.id exists:', !!(window.google && window.google.accounts && window.google.accounts.id));

    verbose('=====================================');
  }
}